// Single source of truth for all EBG service content (Services page, service pages, Home strip, enquiry form).
import {
    Home as HomeIcon, Building2, Globe2, Scale, PiggyBank, Sofa, FileSignature, BarChart3, DoorOpen, Megaphone,
} from "lucide-react";
import { EXTRA_SERVICES } from "./servicesExtra";

const U = (id, w = 2000) => `https://images.unsplash.com/${id}?w=${w}&q=80`;

const PRIMARY = [
    {
        slug: "property-sales", icon: HomeIcon, intent: "buy",
        t: "Property Sales", short: "Buy verified homes, builder projects & resale properties.",
        d: "Residential sales across builder projects and resale homes — shortlisting, site visits, negotiation and closure with full buyer assistance.",
        img: "photo-1741156386380-0236c72eb6f9", eyebrow: "Buy with Confidence",
        subtitle: "From first enquiry to keys in hand — verified residential properties with a dedicated advisor at every step.",
        intro: "Whether you're buying a new home in a builder project or a resale apartment, EBG Realty manages the entire purchase for you. We shortlist only legally verified properties, arrange site visits, negotiate on your behalf and coordinate documentation until possession.",
        offerings: [
            { t: "Residential Property Sales", d: "Apartments, villas, plots and penthouses across our verified inventory." },
            { t: "Builder Projects", d: "New launches and under-construction projects from trusted developers." },
            { t: "Resale Properties", d: "Ready-to-move resale homes with title and society checks done upfront." },
            { t: "Buyer Assistance", d: "Requirement brief, shortlist, comparisons, site visits and negotiation." },
            { t: "Property Enquiries", d: "Quick answers on pricing, availability, payment plans and possession." },
        ],
        benefits: [
            "Only legally verified properties are recommended.",
            "One advisor from shortlist to registration — no hand-offs.",
            "Negotiation support using our builder and channel-partner relationships.",
            "Transparent pricing — no hidden charges.",
        ],
        steps: [
            { h: "Share Your Requirement", d: "Budget, location, configuration and timelines." },
            { h: "Curated Shortlist", d: "Verified options with price, area and possession comparisons." },
            { h: "Site Visits", d: "Guided visits in person or over video." },
            { h: "Negotiation & Booking", d: "Best price and payment plan negotiated on your behalf." },
            { h: "Documentation to Possession", d: "Agreement, loan, registration and handover support." },
        ],
        useCases: ["First-time home buyers", "Upgrading to a larger home", "Buying a resale flat", "Booking in a new launch", "NRIs buying in India"],
        faqs: [
            { q: "Do I pay a fee for buying through EBG?", a: "For builder projects, our fee is typically paid by the developer. For resale deals, a transparent brokerage is agreed in writing upfront." },
            { q: "Can you help with resale and new projects both?", a: "Yes. We handle builder projects, new launches and resale properties." },
        ],
    },
    {
        slug: "commercial-real-estate", icon: Building2, intent: "invest", legacy: ["commercial-solutions"],
        t: "Commercial Real Estate", short: "Offices, retail and business spaces to buy or invest in.",
        d: "Office, retail and commercial properties for businesses and investors — sourcing, due diligence, negotiation and transaction support.",
        img: "photo-1497366216548-37526070297c", eyebrow: "Commercial",
        subtitle: "Offices, retail and business spaces — sourced, verified and negotiated for occupiers and investors.",
        intro: "Commercial property decisions affect your business and your returns. EBG Realty helps companies, founders and investors find the right office, retail or business space — with location analysis, legal checks and commercial negotiation handled end-to-end.",
        offerings: [
            { t: "Office Spaces", d: "Grade-A and Grade-B offices, co-working floors and built-to-suit options." },
            { t: "Retail Spaces", d: "High-street shops, mall units and showroom frontages." },
            { t: "Commercial Properties", d: "Commercial buildings, SCOs, plots and mixed-use assets." },
            { t: "Business Spaces", d: "Clinics, warehouses, studios and industrial units." },
            { t: "Investment Opportunities", d: "Pre-leased and yield-generating commercial assets." },
        ],
        benefits: [
            "Location and footfall analysis before shortlisting.",
            "Title, approvals and zoning checks by our legal desk.",
            "Rental yield and capital-value comparisons for investors.",
            "Negotiation on price, lock-in, escalation and fit-out terms.",
        ],
        steps: [
            { h: "Business Brief", d: "Use, size, budget, location and timelines." },
            { h: "Market Scan", d: "On-market and off-market options compared." },
            { h: "Site Tours", d: "Shortlisted spaces visited with our team." },
            { h: "Due Diligence", d: "Legal, technical and financial checks." },
            { h: "Closure", d: "Term sheet, agreement and handover support." },
        ],
        useCases: ["Company office relocation", "Retail store expansion", "Clinic or studio setup", "Pre-leased asset investment", "Warehouse requirement"],
        faqs: [
            { q: "Do you handle both purchase and lease?", a: "Yes. For leasing, see our Leasing Services; for purchases and investments, this service covers you end-to-end." },
            { q: "Can you find pre-leased properties?", a: "Yes. We share pre-leased options with tenant profile, lease tenure and rental yield." },
        ],
    },
    {
        slug: "nri-property-assistance", icon: Globe2, intent: "nri", legacy: ["nri-investment-desk"],
        t: "NRI Property Assistance", short: "Buy, sell and manage property in India from anywhere.",
        d: "End-to-end support for NRIs — buying, selling, documentation, registration and property management, coordinated remotely.",
        img: "photo-1521791055366-0d553872125f", eyebrow: "NRI Desk",
        subtitle: "Your trusted team on the ground in India — for buying, selling and managing property while you live abroad.",
        intro: "Managing property in India from overseas is hard. EBG Realty's NRI desk acts as your single point of contact — handling site visits on video, paperwork, power of attorney, registration and ongoing management, with regular updates in your time zone.",
        offerings: [
            { t: "Property Buying Assistance", d: "Verified shortlists, video site visits and booking support." },
            { t: "Selling Assistance", d: "Valuation, buyer search, negotiation and sale closure." },
            { t: "Remote Coordination", d: "One relationship manager and updates in your time zone." },
            { t: "Documentation Support", d: "Power of attorney, agreements and FEMA-compliant paperwork." },
            { t: "Registration Support", d: "Registration through your POA holder or during your visit." },
            { t: "Property Management Coordination", d: "Tenant, rent and maintenance coordination after purchase." },
        ],
        benefits: [
            "Live video site visits — no need to fly in.",
            "Guidance on FEMA, repatriation and TDS for NRIs.",
            "Single relationship manager for every property matter.",
            "Regular written updates with photos and documents.",
        ],
        steps: [
            { h: "Video Consultation", d: "Understand your goals and timelines." },
            { h: "Remote Shortlist / Valuation", d: "Options or sale valuation shared digitally." },
            { h: "Virtual Visits", d: "Live walkthroughs on video." },
            { h: "Paperwork & POA", d: "Documentation prepared and coordinated." },
            { h: "Registration & Handover", d: "Completed and reported back to you." },
        ],
        useCases: ["NRI buying a home for parents", "Selling inherited property", "Investing from UAE / UK / US", "Renting out a vacant flat", "Registering via POA"],
        faqs: [
            { q: "Do I need to visit India to buy?", a: "No. Registration can be completed through a power of attorney; we coordinate the full process." },
            { q: "Can you manage my property after purchase?", a: "Yes. We coordinate tenants, rent collection and maintenance through our Property Management team." },
        ],
    },
    {
        slug: "legal-assistance", icon: Scale, intent: "buy",
        t: "Legal Assistance", short: "Legal verification, title review, RERA checks & due diligence.",
        d: "Property legal verification, title and document review, RERA checks, due diligence and registration assistance by our legal desk.",
        img: "photo-1589829545856-d10d557cf95f", eyebrow: "Legal First",
        subtitle: "Know exactly what you're buying — independent legal checks before you sign or pay.",
        intro: "Most property disputes start with a document nobody checked. EBG Realty's legal desk reviews title, approvals and RERA status before you commit, and supports you through agreements and registration — so your purchase is safe and clean.",
        offerings: [
            { t: "Property Legal Verification", d: "Ownership, encumbrance and approvals verified." },
            { t: "Title / Document Review", d: "Chain of title and key documents reviewed by advocates." },
            { t: "RERA Checks", d: "Project registration, timelines and complaint history verified." },
            { t: "Due Diligence", d: "Litigation search, society NOCs and dues checks." },
            { t: "Registration & Documentation Assistance", d: "Agreement review, stamp duty and registration support." },
        ],
        benefits: [
            "Written legal report before you pay a token amount.",
            "Checks done by advocates, not salespeople.",
            "Clear summary of risks in plain language.",
            "Support continues through agreement and registration.",
        ],
        steps: [
            { h: "Share Documents", d: "Send what you have — we request the rest." },
            { h: "Title & Approvals Review", d: "Ownership chain, sanctions and RERA checked." },
            { h: "Searches", d: "Encumbrance and litigation searches." },
            { h: "Legal Report", d: "Findings and recommendations shared in writing." },
            { h: "Agreement & Registration", d: "Drafting, review and registration support." },
        ],
        useCases: ["Before booking a new project", "Buying a resale flat", "Checking a plot or land title", "Reviewing a sale agreement", "Resolving documentation gaps"],
        faqs: [
            { q: "How long does verification take?", a: "Typically 5–7 business days once all documents are available." },
            { q: "Is this a legal guarantee of title?", a: "It's a professional legal opinion based on documents and searches; we explain any risks clearly." },
        ],
    },
    {
        slug: "home-loan", icon: PiggyBank, intent: "buy",
        t: "Home & Property Loans", short: "Home loans, LAP and commercial loans from multiple lenders.",
        d: "Home loans, loan against property and commercial property loans — compare multiple banks and NBFCs and get sanction support.",
        img: "photo-1554224155-6726b3ff858f", eyebrow: "Finance",
        subtitle: "Compare offers from multiple banks and NBFCs and get help from application to disbursement.",
        intro: "The right loan can save you lakhs over its tenure. EBG Realty compares offers from leading banks and NBFCs, helps you prepare documents and coordinates sanction and disbursement — for home purchases, loans against property and commercial property loans.",
        offerings: [
            { t: "Home Loan", d: "For new, under-construction and resale homes." },
            { t: "Loan Against Property", d: "Unlock funds against residential or commercial property." },
            { t: "Commercial Property Loan", d: "Finance for offices, shops and commercial buildings." },
            { t: "Multiple Bank / NBFC Options", d: "Rates and terms compared across lenders." },
            { t: "Loan Enquiry", d: "Eligibility check and indicative EMI in one call." },
        ],
        benefits: [
            "Side-by-side comparison of interest rate, fees and tenure.",
            "Document checklist and file preparation support.",
            "Coordination with the lender through to disbursement.",
            "Balance-transfer advice for existing loans.",
        ],
        steps: [
            { h: "Eligibility Check", d: "Income, credit profile and property assessed." },
            { h: "Lender Comparison", d: "Best-fit offers shortlisted." },
            { h: "Application", d: "Documents prepared and submitted." },
            { h: "Sanction", d: "Follow-up with the lender until sanction." },
            { h: "Disbursement", d: "Coordinated with the builder or seller." },
        ],
        useCases: ["First home purchase", "Under-construction booking", "Loan against owned property", "Buying a shop or office", "Home loan balance transfer"],
        faqs: [
            { q: "Do you charge for loan assistance?", a: "There is usually no charge to you; any applicable fee will be told upfront." },
            { q: "Can NRIs get a home loan?", a: "Yes. Several lenders offer NRI home loans; we help with eligibility and documents." },
        ],
    },
    {
        slug: "interiors", icon: Sofa, intent: "buy",
        t: "Interiors", short: "Interior design, modular kitchens, furniture & turnkey fit-outs.",
        d: "Interior design, modular kitchens, furniture, renovation and turnkey solutions — with a clear quotation before work begins.",
        img: "photo-1567016376408-0226e4d0c1ea", eyebrow: "Design & Build",
        subtitle: "Designer-led interiors with a fixed quotation, clear timelines and one team accountable for delivery.",
        intro: "Moving into a new home or upgrading an old one? EBG Interiors brings designers, carpenters and site supervisors under one roof — so you get a home that's ready to live in, on budget and on time.",
        offerings: [
            { t: "Interior Design", d: "Space planning, 3D designs and material selection." },
            { t: "Modular Kitchen", d: "Functional, durable kitchens with branded hardware." },
            { t: "Furniture", d: "Custom wardrobes, storage and loose furniture." },
            { t: "Renovation", d: "Flooring, painting, false ceiling, electrical and plumbing upgrades." },
            { t: "Turnkey Solutions", d: "Design to handover for homes and offices." },
            { t: "Quotation / Enquiry", d: "Itemised quotation after a site visit." },
        ],
        benefits: [
            "Itemised quotation — no surprise costs.",
            "Single point of contact for the whole project.",
            "Weekly progress updates with photos.",
            "Post-handover support on workmanship.",
        ],
        steps: [
            { h: "Site Visit & Brief", d: "Measurements, needs and budget." },
            { h: "Design & 3D", d: "Layouts and visuals for approval." },
            { h: "Quotation", d: "Itemised cost and timeline agreed." },
            { h: "Execution", d: "Supervised work with progress updates." },
            { h: "Handover", d: "Final walkthrough and snag closure." },
        ],
        useCases: ["New flat possession", "Kitchen upgrade", "Full home renovation", "Rental-ready furnishing", "Office fit-out"],
        faqs: [
            { q: "How long does a full home interior take?", a: "Typically 45–75 days depending on scope, after design sign-off." },
            { q: "Can I get only a modular kitchen or wardrobes?", a: "Yes. We take up individual items as well as full turnkey projects." },
        ],
    },
    {
        slug: "property-documentation", icon: FileSignature, intent: "buy", legacy: ["registration-support"],
        t: "Property Documentation", short: "Sale deeds, agreements, registration & property paperwork.",
        d: "Sale documentation, agreement support, registration and property paperwork — with document verification at every step.",
        img: "photo-1450101499163-c8848c66ca85", eyebrow: "Documentation",
        subtitle: "Accurate paperwork from agreement to registration — so your ownership is clean and complete.",
        intro: "Property paperwork is detailed and unforgiving of mistakes. EBG Realty prepares, reviews and coordinates every document in your transaction — from sale agreement and stamp duty to registration, mutation and society transfer.",
        offerings: [
            { t: "Sale Documentation", d: "Sale deed and supporting documents prepared and reviewed." },
            { t: "Agreement Support", d: "Agreement for sale, leave & licence and MoU drafting/review." },
            { t: "Registration Support", d: "Stamp duty, appointment and sub-registrar coordination." },
            { t: "Property Paperwork", d: "Mutation, society transfer, khata and utility transfers." },
            { t: "Document Verification / Support", d: "Missing or inconsistent documents identified and fixed." },
        ],
        benefits: [
            "Checklist-driven process — nothing missed.",
            "Stamp duty and fee calculation explained upfront.",
            "Coordination with seller, builder, bank and registrar.",
            "Copies organised and shared digitally after completion.",
        ],
        steps: [
            { h: "Document Checklist", d: "What's needed for your transaction." },
            { h: "Drafting & Review", d: "Agreements prepared and checked." },
            { h: "Stamp Duty & Appointment", d: "Payments and registrar slot arranged." },
            { h: "Registration", d: "Accompanied registration at the sub-registrar." },
            { h: "Post-Registration", d: "Mutation, society and utility transfers." },
        ],
        useCases: ["Resale property purchase", "Gift or transfer within family", "Rental agreement registration", "Mutation after purchase", "Fixing missing documents"],
        faqs: [
            { q: "Do you prepare rental agreements too?", a: "Yes, including registered leave & licence agreements." },
            { q: "Can registration be done for NRIs?", a: "Yes, through a power of attorney holder or during your visit to India." },
        ],
    },
    {
        slug: "investment-advisory", icon: BarChart3, intent: "invest",
        t: "Investment Advisory", short: "ROI-focused property investments and portfolio advice.",
        d: "Property investment opportunities with ROI-focused advice — rental yield, appreciation and portfolio planning for buyers and investors.",
        img: "photo-1454165804606-c3d57bc86b40", eyebrow: "Invest Smarter",
        subtitle: "Data-led advice on where, when and what to buy — focused on returns, yield and risk.",
        intro: "Real estate can be a powerful part of your wealth — when you buy right. EBG Realty's investment desk combines market data, rental trends and legal checks to recommend opportunities that match your return goals and risk appetite.",
        offerings: [
            { t: "Property Investment Opportunities", d: "Curated residential and commercial opportunities." },
            { t: "ROI-Focused Advisory", d: "Expected appreciation and total return estimates." },
            { t: "Rental Yield", d: "Yield analysis based on actual rental comparables." },
            { t: "Portfolio Advisory", d: "Diversification across cities, asset types and timelines." },
            { t: "Buyer / Investor Consultation", d: "One-on-one sessions with a senior advisor." },
        ],
        benefits: [
            "Recommendations backed by price and rental data.",
            "Risk level clearly stated for every opportunity.",
            "Exit and resale planning discussed upfront.",
            "Every opportunity legally verified.",
        ],
        steps: [
            { h: "Goals & Budget", d: "Return target, horizon and risk appetite." },
            { h: "Market Analysis", d: "Micro-markets and asset types compared." },
            { h: "Opportunity Shortlist", d: "With ROI, yield and risk notes." },
            { h: "Verification", d: "Legal and technical checks." },
            { h: "Acquisition & Review", d: "Purchase support and periodic portfolio review." },
        ],
        useCases: ["First real estate investment", "Rental income property", "Pre-launch opportunity", "Diversifying an existing portfolio", "NRI investment in India"],
        faqs: [
            { q: "Are returns guaranteed?", a: "No. Real estate returns depend on market conditions; we share data-based estimates and risks clearly." },
            { q: "What budget do I need to start?", a: "Opportunities start from around ₹50 L; we'll suggest options that fit your budget." },
        ],
    },
    {
        slug: "leasing-services", icon: DoorOpen, intent: "rent",
        t: "Leasing Services", short: "Residential, office and retail leasing for owners & tenants.",
        d: "Residential, commercial, retail and office leasing — finding the right tenants for owners and the right space for occupiers.",
        img: "photo-1631193816258-28b44b21e78b", eyebrow: "Leasing",
        subtitle: "Lease your property to verified tenants, or find the right home, office or shop to rent.",
        intro: "Whether you own a property or need one, EBG Realty manages the leasing process end-to-end — from listing and tenant screening to negotiation, agreement and move-in.",
        offerings: [
            { t: "Residential Leasing", d: "Apartments, villas and independent homes for rent." },
            { t: "Commercial Leasing", d: "Commercial buildings and business spaces." },
            { t: "Retail Leasing", d: "Shops, showrooms and mall units for brands and retailers." },
            { t: "Office Leasing", d: "Offices and co-working floors for teams of every size." },
            { t: "Tenant / Occupier Assistance", d: "Requirement brief, shortlisting and negotiation for tenants." },
        ],
        benefits: [
            "Tenant background and document checks.",
            "Market-rent guidance so you neither overprice nor underprice.",
            "Agreement drafting and registration support.",
            "Move-in inspection and handover documentation.",
        ],
        steps: [
            { h: "Requirement / Listing", d: "Owner listing or tenant brief captured." },
            { h: "Marketing & Matching", d: "Property promoted and matches shortlisted." },
            { h: "Visits & Screening", d: "Viewings and tenant verification." },
            { h: "Negotiation", d: "Rent, deposit, lock-in and terms agreed." },
            { h: "Agreement & Move-in", d: "Agreement registered and handover done." },
        ],
        useCases: ["Owner renting out a flat", "Family looking for a rental home", "Startup looking for office space", "Brand seeking a retail store", "NRI leasing a vacant property"],
        faqs: [
            { q: "Do you handle rent collection after leasing?", a: "Yes, through our Property Management service." },
            { q: "Is the rental agreement registered?", a: "We recommend and assist with registered agreements as per local rules." },
        ],
    },
    {
        slug: "property-marketing", icon: Megaphone, intent: "builder",
        t: "Property Marketing & Sales", short: "Project marketing, lead generation & channel-partner sales.",
        d: "Marketing and sales for properties and projects — lead generation, sales support and our channel-partner network for builders and owners.",
        img: "photo-1542744173-8e7e53415bb0", eyebrow: "For Builders & Owners",
        subtitle: "Reach serious buyers faster with marketing, qualified leads and a trusted channel-partner network.",
        intro: "Selling a project or a property takes more than a listing. EBG Realty combines digital marketing, a verified buyer base and a large channel-partner network to generate qualified leads and convert them into sales.",
        offerings: [
            { t: "Property / Project Marketing", d: "Positioning, creatives and campaigns across channels." },
            { t: "Lead Generation", d: "Qualified buyer leads from digital and offline channels." },
            { t: "Sales Support", d: "Site-visit management, follow-ups and closure support." },
            { t: "Channel Partner Network Support", d: "Project access to our verified channel-partner network." },
            { t: "Builder / Developer Marketing Assistance", d: "Launch planning, inventory strategy and sales reporting." },
        ],
        benefits: [
            "Legally verified listings build buyer trust faster.",
            "Leads are qualified before they reach your team.",
            "Transparent reporting on enquiries, visits and bookings.",
            "Access to hundreds of verified channel partners.",
        ],
        steps: [
            { h: "Project Understanding", d: "Inventory, pricing and target buyers." },
            { h: "Marketing Plan", d: "Channels, creatives and budget agreed." },
            { h: "Launch & Leads", d: "Campaigns live and leads qualified." },
            { h: "Channel Activation", d: "Partners briefed and onboarded." },
            { h: "Sales & Reporting", d: "Visits, bookings and regular reports." },
        ],
        useCases: ["New project launch", "Unsold inventory push", "Owner selling a premium property", "Plotted development sales", "Commercial project leasing & sales"],
        faqs: [
            { q: "Do you work with individual owners or only builders?", a: "Both. We market individual premium properties as well as full projects." },
            { q: "How is the fee structured?", a: "Typically a success-based fee; custom marketing retainers are available for projects." },
        ],
    },
];

export const PRIMARY_SERVICES = PRIMARY.map((s) => ({ ...s, primary: true, img: U(s.img, 1400), hero: U(s.img) }));
export const ALL_SERVICES = [...PRIMARY_SERVICES, ...EXTRA_SERVICES];
export { EXTRA_SERVICES };

const LEGACY = Object.fromEntries(PRIMARY_SERVICES.flatMap((s) => (s.legacy || []).map((old) => [old, s.slug])));
export const resolveServiceSlug = (slug) => LEGACY[slug] || slug;
export const getService = (slug) => ALL_SERVICES.find((s) => s.slug === slug);
